import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Calendar, MapPin, Loader2, Tag, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaidEventDetailPage() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [tierId, setTierId] = useState('');
  const [qty, setQty] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [promo, setPromo] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/paid-events/${slug}`).then(r => {
      setEv(r.data);
      const firstAvail = (r.data.ticket_tiers || []).find(t => !t.sold_out);
      if (firstAvail) setTierId(firstAvail.tier_id);
    }).catch(() => setEv(false));
  }, [slug]);

  if (ev === null) return <Layout><div className="max-w-3xl mx-auto p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;
  if (ev === false) return <Layout><div className="max-w-3xl mx-auto p-8 text-center"><h1 className="text-2xl font-bold">Event not found</h1></div></Layout>;

  const tier = ev.ticket_tiers.find(t => t.tier_id === tierId);
  const subtotal = tier ? tier.price * qty : 0;
  const discount = promoApplied ? (promoApplied.discount_type === 'percent' ? subtotal * (promoApplied.discount_value / 100) : Math.min(subtotal, promoApplied.discount_value)) : 0;
  const total = Math.max(0, subtotal - discount);

  const applyPromo = async () => {
    if (!promo) return;
    setCheckingPromo(true); setPromoApplied(null);
    try { const r = await axios.post(`${API}/api/paid-events/${slug}/validate-promo`, { code: promo }); setPromoApplied(r.data); toast.success('Promo applied'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); } finally { setCheckingPromo(false); }
  };

  const checkout = async () => {
    if (!name || !email) return toast.error('Enter your name and email');
    if (!tier) return toast.error('Select a ticket');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/paid-events/${slug}/checkout`, {
        tier_id: tierId, quantity: qty, name, email,
        promo_code: promoApplied ? promo : '', origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); setBusy(false); }
  };

  return (
    <Layout title={ev.title}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        {ev.image_url && <img src={ev.image_url} alt={ev.title} className="w-full h-60 object-cover rounded-2xl mb-6" />}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3" data-testid="event-title">{ev.title}</h1>
        <div className="space-y-1 text-sm text-[#475569] mb-5">
          {ev.starts_at && <div className="flex items-center gap-2"><Calendar size={15} className="text-[#F97316]" />{new Date(ev.starts_at).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}</div>}
          {(ev.venue || ev.location) && <div className="flex items-center gap-2"><MapPin size={15} className="text-[#F97316]" />{[ev.venue, ev.location].filter(Boolean).join(', ')}</div>}
        </div>
        {ev.description && <p className="text-[#334155] leading-relaxed mb-8 whitespace-pre-line">{ev.description}</p>}

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="ticket-box">
          <h2 className="text-lg font-bold text-[#0A0F1C]">Get your ticket</h2>
          <div className="space-y-2">
            {ev.ticket_tiers.map(t => (
              <label key={t.tier_id} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${tierId === t.tier_id ? 'border-[#F97316] bg-orange-50' : 'border-[#E2E8F0]'} ${t.sold_out ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid={`tier-${t.tier_id}`}>
                <input type="radio" name="tier" disabled={t.sold_out} checked={tierId === t.tier_id} onChange={() => setTierId(t.tier_id)} />
                <div className="flex-1"><div className="font-bold text-sm">{t.name} {t.sold_out && <span className="text-xs text-red-600">(Sold out)</span>}</div>{t.description && <div className="text-xs text-[#475569]">{t.description}</div>}</div>
                <div className="font-bold text-[#0F172A]">${t.price}</div>
              </label>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-[#475569] flex flex-col gap-1">Quantity<input type="number" min="1" max="10" value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="px-3 py-2 border rounded text-sm" data-testid="ticket-qty" /></label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="px-3 py-2 border rounded text-sm" placeholder="Full name" value={name} onChange={e => setName(e.target.value)} data-testid="buyer-name" />
            <input className="px-3 py-2 border rounded text-sm" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="buyer-email" />
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input className="w-full pl-9 pr-3 py-2 border rounded text-sm uppercase" placeholder="Promo code" value={promo} onChange={e => { setPromo(e.target.value.toUpperCase()); setPromoApplied(null); }} data-testid="promo-input" />
            </div>
            <button onClick={applyPromo} disabled={checkingPromo || !promo} className="btn-outline text-sm" data-testid="promo-apply">{checkingPromo ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}</button>
          </div>
          {promoApplied && <div className="text-sm text-green-700 flex items-center gap-1" data-testid="promo-success"><Check size={14} />Code {promoApplied.code} applied</div>}

          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-[#475569]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-lg text-[#0A0F1C]"><span>Total</span><span data-testid="ticket-total">${total.toFixed(2)}</span></div>
          </div>

          <button onClick={checkout} disabled={busy || !tier || tier.sold_out} className="btn-primary-orange text-sm w-full" data-testid="checkout-btn">{busy ? <Loader2 size={16} className="animate-spin inline" /> : `Buy ${qty} ticket${qty > 1 ? 's' : ''} · $${total.toFixed(2)}`}</button>
          <p className="text-xs text-center text-[#94A3B8]">Secure checkout via Stripe. You'll get a check-in code by email.</p>
        </div>
      </div>
    </Layout>
  );
}
